/**
 * The "Split your books" choices for a gift of N copies (docs/design/HANDOFF.md:
 * Gift 2a for 2, Gift 2b for 3, Gift 2c for 4 or more). Pure.
 *
 * A choice is { id, title, desc, split } where split is copies per book,
 * e.g. [2, 1]. "mix" has no fixed split: the screen lets the recipient build
 * one with steppers, and `validateSplit` checks it.
 */
export function optionsFor(quantity) {
  const n = Number(quantity) || 0;
  if (n <= 1) return [{ id: "one", title: "1 book", desc: "Make one book.", split: [1] }];
  if (n === 2) {
    return [
      { id: "one", title: "1 book, 2 copies", desc: "Make one book and we print it twice. Great for siblings or grandparents.", split: [2] },
      { id: "two", title: "2 books", desc: "Make two separate books, one copy each.", split: [1, 1] },
    ];
  }
  if (n === 3) {
    return [
      { id: "one", title: "1 book, 3 copies", desc: "Make one book and we print it three times. Great for siblings or grandparents.", split: [3] },
      { id: "split", title: "2 books", desc: "One book with 2 copies, plus a different book with 1 copy.", split: [2, 1] },
      { id: "three", title: "3 different books", desc: "Make three separate books, one copy each.", split: [1, 1, 1] },
    ];
  }
  return [
    { id: "one", title: `1 book, ${n} copies`, desc: `Make one book and we print it ${n} times.`, split: [n] },
    { id: "each", title: `${n} different books`, desc: `Make ${n} separate books, one copy each.`, split: Array(n).fill(1) },
    { id: "mix", title: "Mix and match", desc: "Choose how many books, and how many copies of each.", split: null },
  ];
}

/** The option whose split matches a saved plan, or null. */
export function optionForPlan(options, plan) {
  if (!Array.isArray(plan) || !plan.length) return null;
  const same = (a, b) => a && b && a.length === b.length && a.every((x, i) => x === b[i]);
  return options.find((o) => o.split && same(o.split, plan)) || options.find((o) => o.id === "mix") || null;
}

/** How many copies a mix assigns, and how many are left. */
export function mixStatus(mix, quantity) {
  const assigned = (mix || []).reduce((a, b) => a + (Number(b) || 0), 0);
  return { assigned, left: Math.max(0, quantity - assigned), full: assigned >= quantity };
}

export const summaryRows = (split) => (split || []).map((n, i) => ({ name: `Book ${i + 1}`, copies: `${n} ${n === 1 ? "copy" : "copies"}` }));

export const ctaFor = (split, hasBooks) => {
  if (hasBooks) return "Save split";
  return (split || []).length === 1 ? "Start your book" : "Start book 1";
};
